/**
 * Ce que l'écran doit montrer d'une partie — sélecteurs purs, sans React.
 *
 * Rien ici n'est décoratif. Le § 3.3 du plan et le § 8 du rapport disent la même
 * chose : voir les scores déjà posés vaut **+4,8 points** de taux de défaite. Une
 * information cachée n'est pas un choix d'esthétique, c'est une règle du jeu changée.
 *
 * Le tri, l'ordre du podium et la formulation de la consigne vivent donc ici plutôt
 * que dans le JSX : ils se testent, et ils ne changent pas quand l'habillage change.
 */

import { evaluerMain, terminee, type Partie } from "@zinc421/shared";

import type { Textes } from "../i18n/index.js";
import { nomDeCombinaison } from "./libelles.js";
import { aQuiDeJouer, type Session } from "./session.js";

/**
 * L'enjeu de la phase, en une ligne.
 *
 * ⚠️ « charge » et « décharge » restent tels quels dans les deux langues : ce sont les
 * noms des deux moitiés d'une partie de 421, pas une description. Ce qui les suit, en
 * revanche, se traduit.
 */
export function enjeuDeLaPhase(partie: Partie, textes: Textes): string {
  switch (partie.phase) {
    case "CHARGE": return textes.enjeuCharge;
    case "DECHARGE": return textes.enjeuDecharge;
    default: return textes.partieTerminee;
  }
}

export interface LigneJoueur {
  readonly siege: number;
  readonly nom: string;
  readonly jetons: number;
  readonly estIA: boolean;
  /** C'est à lui d'agir. */
  readonly aLaMain: boolean;
  /** Il s'est débarrassé de ses jetons : il ne joue plus. */
  readonly sauve: boolean;
  /** Ce qu'il a posé dans la manche en cours, ou `null` s'il n'a pas encore parlé. */
  readonly pose: {
    readonly nom: string;
    readonly score: number;
    readonly jetons: number;
    readonly lancers: number;
  } | null;
}

/** Une ligne par siège, dans l'ordre des sièges — l'ordre autour du zinc. */
export function lignesJoueurs(partie: Partie, textes: Textes): readonly LigneJoueur[] {
  const tour = aQuiDeJouer(partie);
  return partie.joueurs.map((joueur) => {
    const pose = partie.manche?.poses[joueur.siege];
    const combinaison = pose ? evaluerMain(pose.main) : null;
    return {
      siege: joueur.siege,
      nom: joueur.nom,
      jetons: joueur.jetons,
      estIA: joueur.estIA,
      aLaMain: tour?.siege === joueur.siege,
      sauve: !partie.actifs.includes(joueur.siege),
      pose: combinaison && pose
        ? {
            nom: nomDeCombinaison(combinaison, textes),
            score: combinaison.score,
            jetons: combinaison.jetons,
            lancers: pose.lancers,
          }
        : null,
    };
  });
}

export interface ScorePose {
  readonly siege: number;
  readonly nom: string;
  readonly combinaison: string;
  readonly score: number;
  readonly jetons: number;
}

/**
 * Les jets déjà posés, du plus fort au plus faible.
 *
 * Trié, et pas seulement listé : c'est sous cette forme que l'information sert. Le
 * joueur qui parle après veut savoir ce qu'il doit battre, ou ce qu'il lui suffit de
 * ne pas être.
 */
export function posesTriees(partie: Partie, textes: Textes): readonly ScorePose[] {
  const manche = partie.manche;
  if (!manche) return [];
  return partie.joueurs
    .filter((j) => manche.poses[j.siege] !== undefined)
    .map((j) => {
      const combinaison = evaluerMain(manche.poses[j.siege]!.main);
      return {
        siege: j.siege,
        nom: j.nom,
        combinaison: nomDeCombinaison(combinaison, textes),
        score: combinaison.score,
        jetons: combinaison.jetons,
      };
    })
    .sort((a, b) => b.score - a.score);
}

/**
 * Vrai quand la fin de partie peut être annoncée.
 *
 * Une partie peut se terminer **sur un lancer** — le dernier jet d'un rampeau. Se
 * fier au seul `terminee()` afficherait le podium par-dessus des dés encore en
 * l'air, et la manche décisive ne serait jamais vue.
 */
export function finVisible(session: Session): boolean {
  return terminee(session.partie) && session.demande === null;
}

export interface RangFinal {
  readonly rang: number;
  readonly siege: number;
  readonly nom: string;
  readonly paieLaTournee: boolean;
}

/** Le podium, du premier sauvé au dernier — celui qui paie sa tournée. */
export function podium(partie: Partie): readonly RangFinal[] {
  const ordre = partie.perdant === null
    ? partie.classement
    : [...partie.classement, partie.perdant];
  return ordre.map((siege, i) => ({
    rang: i + 1,
    siege,
    nom: partie.joueurs[siege]?.nom ?? `#${siege}`,
    paieLaTournee: i === ordre.length - 1 && partie.perdant !== null,
  }));
}

/**
 * La ligne qui dit quoi faire maintenant.
 *
 * Elle nomme toujours qui agit : sans elle, une table de quatre où trois joueurs
 * sont des IA donne l'impression que le jeu s'anime tout seul.
 */
export function consigne(session: Session, textes: Textes): string {
  const partie = session.partie;
  if (session.demande !== null) return textes.desQuiRoulent;
  if (terminee(partie)) {
    const perdant = partie.perdant === null ? null : partie.joueurs[partie.perdant];
    return perdant
      ? textes.paieSaTourneePhrase(perdant.nom) : textes.partieTerminee;
  }

  const tour = aQuiDeJouer(partie);
  if (!tour) return "…";
  const manche = partie.manche!;
  // En hot-seat, la consigne doit dire QUI joue : plusieurs humains se passent le
  // même appareil, et « À toi » ne désigne alors personne.
  const nomme = (texte: string): string => (
    partie.joueurs.filter((j) => !j.estIA).length > 1
      ? textes.aQui(tour.nom, texte)
      // Sans préfixe, la phrase commence la ligne : elle reprend sa majuscule.
      : texte.charAt(0).toUpperCase() + texte.slice(1));

  if (manche.rampeau) {
    return tour.estIA
      ? textes.rampeauLance(tour.nom)
      : nomme(textes.rampeauUnLancer);
  }
  if (tour.estIA) return textes.ilReflechit(tour.nom);

  if (!manche.enCours) {
    return nomme(manche.lancersMax === null
      ? textes.aToiDOuvrir
      : textes.aToi(lancersDits(manche.lancersMax, textes)));
  }
  const reste = (manche.lancersMax ?? 3) - manche.enCours.lancers;
  if (reste <= 0) return nomme(textes.plusDeRelance);
  return nomme(manche.lancersMax === null
    ? textes.gardeOuRelanceFixe(manche.enCours.lancers)
    : textes.gardeOuRelanceReste(reste));
}

function lancersDits(max: number, textes: Textes): string {
  return max === 1 ? textes.mancheSeche : textes.lancersAuMaximum(max);
}
